import React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { JRCFlexBox } from 'components/JRCFlex/JRCFlexBox';
import { JRCLoader } from 'components/JRCLoader/JRCLoader';
import { JRCTypography } from 'components/JRCTypography/JRCTypography';
import { JRCStepper, Step } from 'components/Stepper/JRCStepper';
import { JRCImageStepper } from 'components/Stepper/JRCImageStepper';
import { JRCFadeStepper } from 'components/Stepper/JRCFadeStepper';

export type JRCStepperPageProps = {
    children: React.ReactNode | Iterable<React.ReactNode>;
    activeStep: number;
    imgSrc: string;
    headerContent: React.ReactNode;
    steps?: Array<Step>;
    loadingContent?: React.ReactNode | string;
    isLoading?: boolean;
};

const LoaderWrapper = styled(JRCFlexBox).attrs(() => ({ gap: 16, x: 'center', y: 'center', direction: 'column' }))`
    width: 100%;
    height: fit-content;
    align-self: center;
`;

const Wrapper = styled(JRCFlexBox).attrs(() => ({ gap: 0 }))`
    width: 100%;
    max-width: 100vw;
    height: var(--body-height);
`;

const ContentWrapper = styled(JRCFlexBox).attrs(() => ({ direction: 'column' }))`
    padding: 32px 0 0 24px;
    flex-grow: 1;
    background-color: white;
`;

const BodyContentWrapper = styled(JRCFlexBox).attrs(() => ({ gap: 40 }))`
    flex-grow: 1;
`;

export const JRCStepperPage = (props: JRCStepperPageProps) => {
    const intl = useIntl();

    const childrenArray = React.Children.toArray(props.children);
    const MAX_STEP = childrenArray.length;

    const computeDisplayStep = function () {
        if (props.activeStep < 0) {
            return 0;
        }
        if (props.activeStep >= MAX_STEP) {
            return MAX_STEP - 1;
        }
        return props.activeStep;
    };

    const displayStep = computeDisplayStep();

    return (
        <Wrapper>
            <JRCImageStepper activeStep={displayStep} stepNumber={MAX_STEP} src={props.imgSrc} alt="decoration" />
            <ContentWrapper>
                <div>{props.headerContent}</div>
                <BodyContentWrapper>
                    {props.isLoading ? (
                        typeof props.loadingContent === 'string' ? (
                            <LoaderWrapper>
                                <JRCLoader size="l" />
                                <JRCTypography weight="bold">
                                    {intl.formatMessage({ id: props.loadingContent ?? 'GLOBAL_Loading' })}
                                </JRCTypography>
                            </LoaderWrapper>
                        ) : (
                            props.loadingContent
                        )
                    ) : (
                        <>
                            <JRCStepper steps={props.steps} activeStep={displayStep} />
                            <JRCFadeStepper activeStep={displayStep}>{props.children}</JRCFadeStepper>
                        </>
                    )}
                </BodyContentWrapper>
            </ContentWrapper>
        </Wrapper>
    );
};
